import * as Api from '@ViewModels';
import { ResourceAutoCompleteViewModelType } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { getDisplayName } from '../../../models/NameUtils';
import { useLoadAllUsers } from '../../../queries/User/useLoadAllUsers';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../web/components/autocomplete/SimpleAutoCompleteSearchField';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Modal } from '../../../web/components/Modal';
import { EditItemPenIcon } from '../../../web/components/svgs/icons/EditItemPenIcon';
import { RoundAddIcon } from '../../../web/components/svgs/icons/RoundAddIcon';
import { TrashIcon } from '../../../web/components/svgs/icons/TrashIcon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { baseStyleSheet } from '../../../web/styles/styles';
import { styleSheet } from './styles';

/** Sentinel used in draft state to represent the "Contacts owned by" dynamic filter type */
const CONTACTS_OWNED_BY_FILTER: Api.IOwnershipFilter = { title: '__contacts_owned_by__' };

const FILTER_NONE_ID = 'None';
const FILTER_CONTACTS_OWNED_BY_ID = 'Contacts owned by';

const SENDER_MODE_OPTIONS = [
	{ id: 'sender-mode-current-user', value: Api.SendEmailFrom.CurrentUser, label: 'Current User (sends as themselves)' },
	{ id: 'sender-mode-contact-owner', value: Api.SendEmailFrom.ContactOwner, label: 'Contact Owner' },
	{ id: 'sender-mode-specific-user', value: Api.SendEmailFrom.SpecificUser, label: 'Specific User' },
] as const;

interface IPermissionDraft {
	/** The user who receives the permission */
	userId: string;
	senderMode: Api.ISendEmailFrom;
	/** Required when senderMode is SpecificUser */
	senderUserId?: string;
	/** Display name for the selected sender user, kept in sync with senderUserId */
	senderUserName?: string;
	/** Optional title for the permission itself */
	title?: string;
	/** Optional ownership filter applied when sending */
	requiredFilter?: Api.IOwnershipFilter;
	/** Title for the requiredFilter */
	filterTitle?: string;
	/** User IDs for the "Contacts owned by" dynamic filter; populated when requiredFilter === CONTACTS_OWNED_BY_FILTER */
	contactsOwnedByUserIds?: string[];
	/** True when editing an existing row — user picker is locked */
	isEditing: boolean;
}

export interface ISendOnBehalfPermissionsPanelProps {
	impersonationContext: Api.IImpersonationContext;
}

/**
 * Manage customSendOnBehalfPermissions per user on an account.
 * One permission row is allowed per user. Rows can be added, edited, or removed.
 */
export const SendOnBehalfPermissionsPanel: React.FC<ISendOnBehalfPermissionsPanelProps> = ({
	impersonationContext,
}) => {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('SendOnBehalfPermissionsPanel');
	const [draft, setDraft] = React.useState<IPermissionDraft | null>(null);
	const [showSenderSearch, setShowSenderSearch] = React.useState(false);
	const [showOwnerSearch, setShowOwnerSearch] = React.useState(false);
	const [saving, setSaving] = React.useState(false);
	const [search, setSearch] = React.useState('');

	const { users, isLoading, updateUser } = useLoadAllUsers({ impersonationContext });

	const usersWithPermissions = React.useMemo(
		() => users.filter(u => u.customSendOnBehalfPermissions?.length > 0),
		[users]
	);

	const filteredUsersWithPermissions = React.useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) {
			return usersWithPermissions;
		}
		return usersWithPermissions.filter(u => getDisplayName(u).toLowerCase().includes(query));
	}, [usersWithPermissions, search]);

	/** Users that can be assigned a new permission (excludes those who already have one) */
	const addableUsers = React.useMemo(() => {
		const usersWithPermissionIds = new Set(usersWithPermissions.map(u => u.id));
		return users.filter(u => !usersWithPermissionIds.has(u.id));
	}, [users, usersWithPermissions]);

	const selectedFilterId = React.useMemo(() => {
		if (!draft?.requiredFilter) {
			return FILTER_NONE_ID;
		}
		if (draft.requiredFilter === CONTACTS_OWNED_BY_FILTER) {
			return FILTER_CONTACTS_OWNED_BY_ID;
		}
		return FILTER_NONE_ID;
	}, [draft?.requiredFilter]);

	const onAddPermission = () => {
		setDraft({
			isEditing: false,
			senderMode: Api.SendEmailFrom.CurrentUser,
			userId: '',
		});
	};

	const onEditPermission = (user: Api.IUser) => {
		const permission = user.customSendOnBehalfPermissions?.[0];
		const senderUser = permission?.sender?.userId ? users.find(u => u.id === permission.sender.userId) : undefined;
		const isOwnedBy = permission?.requiredFilter?.criteria?.property === Api.ContactFilterCriteriaProperty.OwnedBy;
		setDraft({
			isEditing: true,
			userId: user.id,
			title: permission?.title,
			senderMode: permission?.sender?.mode ?? Api.SendEmailFrom.CurrentUser,
			senderUserId: permission?.sender?.userId,
			senderUserName: senderUser ? getDisplayName(senderUser) : undefined,
			requiredFilter: isOwnedBy ? CONTACTS_OWNED_BY_FILTER : permission?.requiredFilter,
			filterTitle: permission?.requiredFilter?.title,
			contactsOwnedByUserIds: isOwnedBy ? Object.keys(permission.requiredFilter?.criteria?.values ?? {}) : undefined,
		});
	};

	const onSenderUserSearchSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent) => {
		const user = e.selection as Api.IUser;
		setDraft(prev => ({
			...prev,
			senderUserId: user.id,
			senderUserName: getDisplayName(user),
		}));
		setShowSenderSearch(false);
	};

	const onOwnerSearchSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent) => {
		const user = e.selection as Api.IUser;
		setDraft(prev => ({
			...prev,
			contactsOwnedByUserIds: [...(prev.contactsOwnedByUserIds ?? []).filter(id => id !== user.id), user.id],
		}));
		setShowOwnerSearch(false);
	};

	const onRemoveOwner = (userId: string) => {
		setDraft(prev => ({
			...prev,
			contactsOwnedByUserIds: (prev.contactsOwnedByUserIds ?? []).filter(id => id !== userId),
		}));
	};

	const savePermissions = async (
		user: Api.IUser,
		permissions: Api.ISendOnBehalfPermission[],
		successMessage: string
	) => {
		setSaving(true);
		try {
			await userSession.webServiceHelper.callAsync<void>(
				`admin/userOperations/${encodeURIComponent(user.id)}/setSendOnBehalfPermissions`,
				'POST',
				permissions
			);
			setDraft(null);
			updateUser({ ...user, customSendOnBehalfPermissions: permissions });
			toast.success(successMessage);
		} catch (err) {
			logApiError('SaveSendOnBehalfPermission', err as Api.IOperationResultNoValue);
			toast.error('Failed to save permissions. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	const onSaveDraft = async () => {
		if (!draft?.userId) {
			return;
		}
		if (draft.senderMode === Api.SendEmailFrom.SpecificUser && !draft.senderUserId) {
			toast.error('Please select a sender user.');
			return;
		}

		const targetUser = users.find(u => u.id === draft.userId);
		if (!targetUser) {
			return;
		}

		const sender: Api.ISendFromOptions = {
			mode: draft.senderMode,
			...(draft.senderMode === Api.SendEmailFrom.SpecificUser && draft.senderUserId
				? { userId: draft.senderUserId }
				: {}),
		};

		let requiredFilter: Api.IOwnershipFilter | undefined;
		if (draft.requiredFilter === CONTACTS_OWNED_BY_FILTER) {
			if (draft.contactsOwnedByUserIds?.length) {
				requiredFilter = {
					...(draft.filterTitle ? { title: draft.filterTitle } : {}),
					criteria: {
						property: Api.ContactFilterCriteriaProperty.OwnedBy,
						values: Object.fromEntries(draft.contactsOwnedByUserIds.map(id => [id, ''])),
					},
				};
			}
		} else if (draft.requiredFilter) {
			requiredFilter = { ...draft.requiredFilter, ...(draft.filterTitle ? { title: draft.filterTitle } : {}) };
		}

		const permission: Api.ISendOnBehalfPermission = {
			sender,
			...(draft.title ? { title: draft.title } : {}),
			...(requiredFilter ? { requiredFilter } : {}),
		};

		await savePermissions(targetUser, [permission], `Permissions saved for ${getDisplayName(targetUser)}.`);
	};

	const onDeletePermission = async (user: Api.IUser) => {
		await savePermissions(user, [], `Permissions removed for ${getDisplayName(user)}.`);
	};

	const onCancelDraft = () => {
		setDraft(null);
	};

	const canSave = !!draft?.userId && (draft.senderMode !== Api.SendEmailFrom.SpecificUser || !!draft.senderUserId);

	const getSenderLabel = (permission: Api.ISendOnBehalfPermission) => {
		if (!permission.sender || permission.sender.mode === Api.SendEmailFrom.CurrentUser) {
			return 'Current User';
		}
		if (permission.sender.mode === Api.SendEmailFrom.ContactOwner) {
			return 'Contact Owner';
		}
		if (permission.sender.mode === Api.SendEmailFrom.SpecificUser) {
			const senderUser = users.find(u => u.id === permission.sender.userId);
			return senderUser ? `Specific User: ${getDisplayName(senderUser)}` : 'Specific User';
		}
		return permission.sender.mode;
	};

	const draftUserName = React.useMemo(() => {
		if (!draft?.userId) {
			return '';
		}
		const user = users.find(u => u.id === draft.userId);
		return user ? getDisplayName(user) : '';
	}, [draft?.userId, users]);

	const renderForm = () => (
		<div className={css(styleSheet.formModal)}>
			{draft.isEditing && (
				<div className={css(styleSheet.editingForLabel)}>
					{'Editing permissions for '}
					<strong>{draftUserName}</strong>
				</div>
			)}

			{!draft.isEditing && (
				<div className={css(styleSheet.fieldRow)}>
					<div className={css(styleSheet.fieldLabel)}>User receiving permission</div>
					<Select
						value={draft.userId || null}
						onValueChange={userId => {
							setDraft(prev => ({ ...prev, userId, requiredFilter: undefined, contactsOwnedByUserIds: undefined }));
						}}
					>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<SelectValue placeholder='Select user...'>
								{draft.userId ? getDisplayName(addableUsers.find(u => u.id === draft.userId)) : undefined}
							</SelectValue>
						</SelectTrigger>
						<SelectContent>
							{addableUsers.map(u => (
								<SelectItem key={u.id} value={u.id}>
									{getDisplayName(u)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			)}

			<div className={css(styleSheet.formSection)}>
				<div className={css(styleSheet.formSectionHeading)}>Sender</div>

				<div className={css(styleSheet.fieldRow)}>
					<div className={css(styleSheet.fieldLabel)}>Title</div>
					<input
						className={css(styleSheet.textInput)}
						onChange={e => setDraft(prev => ({ ...prev, title: e.target.value || undefined }))}
						placeholder='Optional label for this permission'
						type='text'
						value={draft.title ?? ''}
					/>
				</div>

				<div className={css(styleSheet.fieldRow)}>
					<div className={css(styleSheet.fieldLabel)}>Send as</div>
					<Select
						value={draft.senderMode}
						onValueChange={value => {
							const mode = value as Api.ISendEmailFrom;
							const isSpecificUser = mode === Api.SendEmailFrom.SpecificUser;
							setDraft(prev => ({
								...prev,
								senderMode: mode,
								senderUserId: isSpecificUser ? prev.senderUserId : undefined,
								senderUserName: isSpecificUser ? prev.senderUserName : undefined,
							}));
							if (isSpecificUser) {
								setShowSenderSearch(true);
							}
						}}
					>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<SelectValue>{SENDER_MODE_OPTIONS.find(o => o.value === draft.senderMode)?.label}</SelectValue>
						</SelectTrigger>
						<SelectContent>
							{SENDER_MODE_OPTIONS.map(o => (
								<SelectItem key={o.id} value={o.value}>
									{o.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{draft.senderMode === Api.SendEmailFrom.SpecificUser && draft.senderUserName && (
						<div className={css(styleSheet.specificUserLabel)}>{draft.senderUserName}</div>
					)}
					{draft.senderMode === Api.SendEmailFrom.SpecificUser && !draft.senderUserName && (
						<button className={css(styleSheet.addOwnerButton)} onClick={() => setShowSenderSearch(true)} type='button'>
							Select user...
						</button>
					)}
				</div>
			</div>

			<div className={css(styleSheet.formSection)}>
				<div className={css(styleSheet.formSectionHeading)}>Required Filter</div>

				{draft.userId ? (
					<>
						<div className={css(styleSheet.fieldRow)}>
							<div className={css(styleSheet.fieldLabel)}>Filter</div>
							<Select
								value={selectedFilterId}
								onValueChange={filterId => {
									const isOwnedBy = filterId === FILTER_CONTACTS_OWNED_BY_ID;
									setDraft(prev => ({
										...prev,
										requiredFilter: isOwnedBy ? CONTACTS_OWNED_BY_FILTER : undefined,
										filterTitle: undefined,
										contactsOwnedByUserIds: isOwnedBy ? (prev.contactsOwnedByUserIds ?? []) : undefined,
									}));
								}}
							>
								<SelectTrigger styles={styleSheet.selectTrigger}>
									<SelectValue>
										{selectedFilterId === FILTER_CONTACTS_OWNED_BY_ID ? 'Contacts owned by...' : 'None'}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={FILTER_NONE_ID}>None</SelectItem>
									<SelectItem value={FILTER_CONTACTS_OWNED_BY_ID}>Contacts owned by...</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{draft.requiredFilter === CONTACTS_OWNED_BY_FILTER && (
							<div className={css(styleSheet.fieldRow)}>
								<div className={css(styleSheet.fieldLabel)}>Owned by</div>
								<div className={css(styleSheet.ownerList)}>
									{(draft.contactsOwnedByUserIds ?? []).map(id => {
										const user = users.find(u => u.id === id);
										return (
											<div key={id} className={css(styleSheet.ownerChip)}>
												<span>{user ? getDisplayName(user) : id}</span>
												<button
													className={css(styleSheet.ownerChipRemove)}
													onClick={() => onRemoveOwner(id)}
													title='Remove'
													type='button'
												>
													{'×'}
												</button>
											</div>
										);
									})}
									<button
										className={css(styleSheet.addOwnerButton)}
										onClick={() => setShowOwnerSearch(true)}
										type='button'
									>
										{'+ Add user'}
									</button>
								</div>
							</div>
						)}

						{draft.requiredFilter && (
							<div className={css(styleSheet.fieldRow)}>
								<div className={css(styleSheet.fieldLabel)}>Filter title</div>
								<input
									className={css(styleSheet.textInput)}
									onChange={e => setDraft(prev => ({ ...prev, filterTitle: e.target.value || undefined }))}
									placeholder='Optional label for this filter'
									type='text'
									value={draft.filterTitle ?? ''}
								/>
							</div>
						)}
					</>
				) : (
					<div className={css(styleSheet.formSectionEmpty)}>Select a user above to configure a filter.</div>
				)}
			</div>

			<div className={css(styleSheet.formActions)}>
				<button className={css(baseStyleSheet.ctaButton)} disabled={!canSave || saving} onClick={onSaveDraft}>
					{saving ? 'Saving...' : 'Save'}
				</button>
				<button className={css(baseStyleSheet.ctaButtonReverse)} disabled={saving} onClick={onCancelDraft}>
					Cancel
				</button>
			</div>
		</div>
	);

	return (
		<>
			<div className={css(styleSheet.pagePanel)}>
				{isLoading ? (
					<LoadingSpinner />
				) : (
					<>
						<div className={css(styleSheet.title)}>Manage Send on Behalf Permissions</div>

						{usersWithPermissions.length > 0 && (
							<input
								className={css(styleSheet.textInput)}
								onChange={e => setSearch(e.target.value)}
								placeholder='Search by name...'
								type='text'
								value={search}
							/>
						)}

						{usersWithPermissions.length === 0 && (
							<div className={css(styleSheet.emptyState)}>
								No custom send on behalf permissions configured. Click below to add one.
							</div>
						)}

						{filteredUsersWithPermissions.map(user => {
							const permission = user.customSendOnBehalfPermissions[0];
							return (
								<div key={user.id} className={css(styleSheet.permissionRow)}>
									<div className={css(styleSheet.permissionRowDetails)}>
										<div className={css(styleSheet.permissionRowTitle)}>{getDisplayName(user)}</div>
										<div className={css(styleSheet.permissionRowSubtitle)}>
											{'Sender: '}
											{getSenderLabel(permission)}
										</div>
										{permission.requiredFilter?.title && (
											<div className={css(styleSheet.permissionRowSubtitle)}>
												{`Filter: ${permission.requiredFilter.title}`}
											</div>
										)}
									</div>
									<button
										className={css(styleSheet.iconButton)}
										disabled={saving}
										onClick={() => onEditPermission(user)}
										title='Edit permission'
									>
										<EditItemPenIcon />
									</button>
									<button
										className={css(styleSheet.iconButton, styleSheet.deleteButton)}
										disabled={saving}
										onClick={() => onDeletePermission(user)}
										title='Remove permission'
									>
										<TrashIcon />
									</button>
								</div>
							);
						})}

						<button className={css(styleSheet.addButton)} onClick={onAddPermission}>
							<RoundAddIcon />
							<span>Add Permission</span>
						</button>
					</>
				)}
			</div>

			<Modal isOpen={!!draft} onRequestClose={onCancelDraft} useDefaultHeader useStyledHeader>
				{!!draft && renderForm()}
			</Modal>

			<Modal isOpen={showSenderSearch} onRequestClose={() => setShowSenderSearch(false)} useDefaultHeader={false}>
				<div className={css(styleSheet.senderSearchModal)}>
					<div className={css(styleSheet.title)}>Select Sender User</div>
					<SimpleAutoCompleteSearchField
						impersonationContext={impersonationContext}
						onItemSelected={onSenderUserSearchSelected}
						pageSize={8}
						placeholder='Search users...'
						resultsLimit={8}
						type={ResourceAutoCompleteViewModelType.User}
					/>
				</div>
			</Modal>

			<Modal isOpen={showOwnerSearch} onRequestClose={() => setShowOwnerSearch(false)} useDefaultHeader={false}>
				<div className={css(styleSheet.senderSearchModal)}>
					<div className={css(styleSheet.title)}>Add Owner</div>
					<SimpleAutoCompleteSearchField
						impersonationContext={impersonationContext}
						onItemSelected={onOwnerSearchSelected}
						pageSize={8}
						placeholder='Search users...'
						resultsLimit={8}
						type={ResourceAutoCompleteViewModelType.User}
					/>
				</div>
			</Modal>
		</>
	);
};
